import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import {
  SubmissionSummaryViewModel,
  QuoteBreakdownViewModel,
  MGAPremiumBreakDownDetailViewModel,
  DocumentGridRowViewModel,
  PolicyChangeGridViewModel,
  LossRunsRequestGridViewModel,
  LossRunsRequestREQ,
  LossRunsRequestRES,
  UpdateProducerViewModel,
} from '@program-suite/data-common';
import { LookupViewModel } from '@program-suite/data-common/program';

// Temporary type definition until API models are regenerated
interface PriorWorkViewModel {
  removePriorWork?: boolean;
  priorWorksEffectiveDate?: string | null;
  issuedEffectiveDate?: string | Date | null;
}

@Injectable({
  providedIn: 'root',
})
export class ViewSubmissionService {
  http = inject(HttpClient);

  //#region View Submission Endpoints
  /**
   * Get submission summary
   * @param submissionGuid - The GUID of the submission
   * @returns 200 - OK(SubmissionSummaryViewModel)
   * @throws Error if fails
   */
  async getSubmissionSummary(submissionGuid: string): Promise<SubmissionSummaryViewModel> {
    try {
      return await firstValueFrom(
        this.http.get<SubmissionSummaryViewModel>(`submission/summary/${submissionGuid}`, { responseType: 'json' }),
      );
    } catch (error) {
      throw new Error('Something went wrong, please try again');
    }
  }

  /**
   * Get submission review
   * @param submissionGuid - The GUID of the submission
   * @returns 200 - OK(SubmissionSummaryViewModel)
   * @throws Error if fails
   */
  async getSubmissionReview(submissionGuid: string): Promise<SubmissionSummaryViewModel> {
    try {
      return await firstValueFrom(
        this.http.get<SubmissionSummaryViewModel>(`submission/submission-review/${submissionGuid}`, {
          responseType: 'json',
        }),
      );
    } catch (error) {
      throw new Error('Something went wrong, please try again');
    }
  }

  /**
   * Get submission pricing totals
   * @param submissionGuid - The GUID of the submission
   * @returns 200 - OK(QuoteBreakdownViewModel)
   * @throws Error if fails
   */
  async getSubmissionPricingTotals(submissionGuid: string): Promise<QuoteBreakdownViewModel> {
    try {
      return await firstValueFrom(
        this.http.get<QuoteBreakdownViewModel>(`submission/policy-pricing-totals/${submissionGuid}`, {
          responseType: 'json',
        }),
      );
    } catch (error) {
      throw new Error('Something went wrong, please try again');
    }
  }

  /**
   * Get submission premium breakdown
   * @param submissionGuid - The GUID of the submission
   * @returns 200 - OK(MGAPremiumBreakDownDetailViewModel)
   * @throws Error if fails
   */
  async getSubmissionPremiumBreakdown(submissionGuid: string): Promise<MGAPremiumBreakDownDetailViewModel> {
    try {
      return await firstValueFrom(
        this.http.get<MGAPremiumBreakDownDetailViewModel>(`submission/premium-breakdown/${submissionGuid}`, {
          responseType: 'json',
        }),
      );
    } catch (error) {
      throw new Error('Something went wrong, please try again');
    }
  }

  /**
   * Get submission documents
   * @param submissionGuid - The GUID of the submission
   * @returns 200 - OK(List<DocumentViewModel>)
   * @throws Error if fails
   */
  async getSubmissionDocuments(submissionGuid: string): Promise<DocumentGridRowViewModel[]> {
    try {
      return await firstValueFrom(
        this.http.get<DocumentGridRowViewModel[]>(`document/submission/${submissionGuid}`, { responseType: 'json' }),
      );
    } catch (error) {
      throw new Error('Something went wrong, please try again');
    }
  }

  async downloadDocument(documentGuid: string, policyGuid?: string): Promise<void> {
    var docUrl = `document/${documentGuid}`;
    if (policyGuid) {
      docUrl = `document/${documentGuid}/${policyGuid}`;
    }
    try {
      // Fetch the file as a Blob
      const res = await firstValueFrom(
        this.http.get(`${docUrl}`, { responseType: 'blob' as 'json', observe: 'response' }),
      );

      if (!res?.body) {
        throw new Error('Failed to download the document, response body is null');
      }

      // Extract filename from Content-Disposition header
      const contentDisposition = res.headers.get('content-disposition');
      const fileNameMatch = contentDisposition?.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      const fileName = fileNameMatch ? decodeURIComponent(fileNameMatch[1].replace(/['"]/g, '')) : 'downloaded-file';

      // Create a Blob and trigger the download
      const contentType = (res.body as Blob).type;
      const blob = new Blob([res.body as Blob], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading document:', error);
      // The document endpoint responds with a blob, so a rejected request carries its
      // message as a Blob. Surface the server's explanation for the known Conflict case
      // (e.g. a flat cancelled policy that cannot be downloaded).
      if (error instanceof HttpErrorResponse && error.status === 409 && error.error instanceof Blob) {
        const text = await error.error.text();
        throw new Error(this.parseErrorMessage(text));
      }
      throw new Error('Failed to download the document, please try again');
    }
  }

  private parseErrorMessage(body: string): string {
    try {
      return JSON.parse(body);
    } catch {
      return body || 'Failed to download the document, please try again';
    }
  }

  async uploadSubmissionDocument(model: any): Promise<void> {
    try {
      await firstValueFrom(
        this.http.post<void>('document/upload-submission', model, {
          responseType: 'json',
        }),
      );
    } catch (error) {
      console.error('Error uploading submission document:', error);
      throw new Error('Failed to upload the document, please try again');
    }
  }

  async uploadPolicySubmissionDocument(model: any): Promise<void> {
    try {
      await firstValueFrom(
        this.http.post<void>('document/upload-policy-submission', model, {
          responseType: 'json',
        }),
      );
    } catch (error) {
      console.error('Error uploading policy submission document:', error);
      throw new Error('Failed to upload the document, please try again');
    }
  }

  async deleteSubmissionDocument(document: DocumentGridRowViewModel): Promise<void> {
    try {
      await firstValueFrom(this.http.delete<void>('document', { body: document }));
    } catch (error) {
      console.error('Error deleting submission document:', error);
      throw new Error('Failed to delete the document, please try again');
    }
  }

  async updateSubmissionStatus(submissionGuid: string, status: string): Promise<void> {
    try {
      await firstValueFrom(this.http.post<void>(`submission/updatestatus/${submissionGuid}/${status}`, null));
    } catch (error) {
      console.error('Error updating submission status:', error);
      throw new Error('Failed to update the submission status, please try again');
    }
  }

  async updateSubmissionProducer(submissionGuid: string, data: any) {
    try {
      // Prepare the model for the API using UpdateProducerViewModel interface
      const model: UpdateProducerViewModel = {};

      // Check if data is just a producerId (number) or an object with fields
      if (typeof data === 'number') {
        model.agentId = data;
      } else if (data.producerId) {
        model.agentId = data.producerId;
      } else {
        // Map adhoc fields to the UpdateProducerViewModel properties
        model.agencyName = data.agencyName;
        model.producerName = data.producerName;
        model.agencyAddress1 = data.agencyAddress1;
        model.agencyAddress2 = data.agencyAddress2;
        model.agencyCity = data.agencyCity;
        model.agencyStateCode = data.agencyStateCode;
        model.agencyZipCode = data.agencyZipCode;
      }

      await firstValueFrom(this.http.post<void>(`submission/update-agent/${submissionGuid}`, model));
    } catch (error) {
      console.error('Error updating submission producer:', error);
      throw new Error('Failed to update the submission producer, please try again');
    }
  }

  async submitBindRequest(model: any): Promise<void> {
    try {
      await firstValueFrom(
        this.http.post<void>('submission/bind-request', model, {
          responseType: 'json',
        }),
      );
    } catch (error) {
      console.error('Error updating submission status and proposed effective date:', error);
      throw new Error('Failed to update the submission status and proposed effective date, please try again');
    }
  }

  //#endregion

  //#region View Policy Endpoints

  /**
   * Get policy summary
   * @param policyGuid - The GUID of the policy
   * @returns 200 - OK(SubmissionSummaryViewModel)
   * @throws Error if fails
   */
  async getPolicySummary(policyGuid: string): Promise<SubmissionSummaryViewModel> {
    try {
      return await firstValueFrom(
        this.http.get<SubmissionSummaryViewModel>(`policy/summary/${policyGuid}`, { responseType: 'json' }),
      );
    } catch (error) {
      throw new Error('Something went wrong, please try again');
    }
  }

  /**
   * Get policy pricing totals
   * @param policyGuid - The GUID of the policy
   * @returns 200 - OK(QuoteBreakdownViewModel)
   * @throws Error if fails
   */
  async getPolicyPricingTotals(policyGuid: string): Promise<QuoteBreakdownViewModel> {
    try {
      return await firstValueFrom(
        this.http.get<QuoteBreakdownViewModel>(`policy/policy-pricing-totals/${policyGuid}`, { responseType: 'json' }),
      );
    } catch (error) {
      throw new Error('Something went wrong, please try again');
    }
  }

  /**
   * Get policy premium breakdown
   * @param policyGuid - The GUID of the policy
   * @returns 200 - OK(MGAPremiumBreakDownDetailViewModel)
   * @throws Error if fails
   */
  async getPolicyPremiumBreakdown(policyGuid: string): Promise<MGAPremiumBreakDownDetailViewModel> {
    try {
      return await firstValueFrom(
        this.http.get<MGAPremiumBreakDownDetailViewModel>(`policy/premium-breakdown/${policyGuid}`, {
          responseType: 'json',
        }),
      );
    } catch (error) {
      throw new Error('Something went wrong, please try again');
    }
  }

  /**
   * Post issue policy
   * @param submissionGuid - The GUID of the submission
   * @returns 200 - OK(submissionGuid)
   * @throws Error if fails
   */
  async issuePolicy(submissionGuid: string, pwvm: PriorWorkViewModel): Promise<string> {
    try {
      return await firstValueFrom(
        this.http.post<string>(`policy/issue/${submissionGuid}`, pwvm, { responseType: 'json' }),
      );
    } catch (error) {
      throw new Error('Something went wrong, please try again');
    }
  }

  /**
   * Get policy documents
   * @param policyGuid - The GUID of the policy
   * @returns 200 - OK(List<DocumentViewModel>)
   * @throws Error if fails
   */
  async getPolicyDocuments(policyGuid: string): Promise<DocumentGridRowViewModel[]> {
    try {
      return await firstValueFrom(
        this.http.get<DocumentGridRowViewModel[]>(`document/policy/${policyGuid}`, { responseType: 'json' }),
      );
    } catch (error) {
      throw new Error('Something went wrong, please try again');
    }
  }

  /**
   * Get policy change grid
   * @param criteria PolicyChangeCriteriaViewModel containing filter criteria
   * @returns PolicyChangeGridViewModel containing grid data
   * @throws Error if unauthorized or request fails
   */

  async getEndorsements(criteria: any): Promise<PolicyChangeGridViewModel> {
    try {
      const params = { ...criteria }; // Convert criteria to query parameters
      return await firstValueFrom(this.http.get<PolicyChangeGridViewModel>('policyChange/grid', { params }));
    } catch (error) {
      throw new Error('Failed to get policy change grid');
    }
  }

  /**
   * Get policy Submission documents
   * @param policyGuid - The GUID of the policy
   * @returns 200 - OK(List<DocumentViewModel>)
   * @throws Error if fails
   */
  async getPolicySubmissionDocuments(policyGuid: string): Promise<DocumentGridRowViewModel[]> {
    try {
      return await firstValueFrom(
        this.http.get<DocumentGridRowViewModel[]>(`document/policy/submission/${policyGuid}`, { responseType: 'json' }),
      );
    } catch (error) {
      throw new Error('Something went wrong, please try again');
    }
  }
  
  async downloadLossRun(lossRunGuid: string): Promise<void> {
    try {
       // Fetch the file as a Blob
      const res = await firstValueFrom(
        this.http.get(`LossRuns/download/${lossRunGuid}`, { responseType: 'blob' as 'json', observe: 'response' }),
      );

      if (!res?.body) {
        throw new Error('Failed to download the document, response body is null');
      }
        // Extract filename from Content-Disposition header
      const contentDisposition = res.headers.get('content-disposition');
      const fileNameMatch = contentDisposition?.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      const fileName = fileNameMatch ? decodeURIComponent(fileNameMatch[1].replace(/['"]/g, '')) : 'downloaded-file';

      // Create a Blob and trigger the download
      const contentType = (res.body as Blob).type;
      const blob = new Blob([res.body as Blob], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading document:', error);
      throw new Error('Failed to download the document, please try again');
    }
   
  }

  /**
   * get loss runs
   * @returns 200 - OK(LossRunsRequestGridViewModel)
   * @throws Error if fails
   */
  async getLossRuns(params: any): Promise<LossRunsRequestGridViewModel> {
    try {
      return await firstValueFrom(this.http.get('LossRuns', { params, responseType: 'json' }));
    } catch (error) {
      throw new Error('Something went wrong, please try again');
    }
  }

  async getPolicyTermsLookup(policyGuid: string): Promise<LookupViewModel[]> {
    try {
      return await firstValueFrom(
        this.http.get<LookupViewModel[]>(`lossruns/policyterms/${policyGuid}`, { responseType: 'json' }),
      );
    } catch (error) {
      throw new Error('Failed to fetch policy terms lookup');
    }
  }

  async submitLossRunRequest(model: LossRunsRequestREQ): Promise<LossRunsRequestRES> {
    try {
      return await firstValueFrom(
        this.http.post<LossRunsRequestRES>('LossRuns', model, {
          responseType: 'json',
        }),
      );
    } catch (error) {
      throw new Error('Failed to submit the loss run request, please try again');
    }
  }
  //#endregion
}
